import LucidMascot from "@/assets/images/lucid-mascot.png";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useAuth from "@/hooks/auth";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  createStripeCheckout,
  createStripeCheckoutForGuest,
} from "@/services/api/Stripe";
import { checkCoupon, createAbandonedCart } from "@/services/api/User";
import { loadStripe } from "@stripe/stripe-js";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { useState, useEffect, useRef } from "react";
import { loginPopup, purchasePopup } from "@/Atoms";
import productsDev from "@/hooks/products.dev";
import productsProd from "@/hooks/products.prod";
import OnUpgradeAtStripeCheckout from "./OnUpgradeAtStripeCheckout";
import ModalActivateSubscription from "../ModalActivateSubscription";
import toast from "react-hot-toast";
import ModalWaitPaymentUpgrade from "../ModalWaitPaymentUpgrade";
import {
  Bookmark,
  Check,
  Heart,
  Info,
  X,
} from "@phosphor-icons/react/dist/ssr";
import { useAtom } from "jotai";
import { convertStripeAmount } from "@/utils/currency";

const products = process.env.ENV !== "production" ? productsDev : productsProd;

const PlanCard = ({
  plan,
  benefits,
  highlighted,
  hideHighligtedGraphic,
  currencyCode,
  priceData,
  priceDataMonthly,
  planSubscription,
  setBillingCycle,
  billingCycle,
  _priceData,
  onlyButton,
  compactOnlyButton,
  customButtonText,
  disabled,
  description,
  hasVCAngelSelection,
  externalInvestorValue,
}) => {
  const { user } = useAuth();
  const stripePromise = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
  );

  const router = useRouter();
  const [inputCoupon, setInputCoupon] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState(false);
  const [loadingCheck, setloadingCheck] = useState(false);
  const [priceId, setPriceId] = useState(null);
  const [openLoginPopup, setOpenLoginPopup] = useAtom(loginPopup);
  const [openPurchasePopup, setOpenPurchasePopup] = useAtom(purchasePopup);
  const [isCoupon, setIsCoupon] = useState(false);
  const [couponErrorProduct, setCouponErrorProduct] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("startup");
  const [open, setOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenActivateFullsubscription, setIsOpenActivateFullsubscription] =
    useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const postLoginCheckCompleteRef = useRef(false);
  const [emailPayloadForGuest, setEmailPayloadForGuest] = useState("");
  const [investorValueInternal, setInvestorValue] = useState("vc"); // vc,angel
  const investorValue = externalInvestorValue ?? investorValueInternal;

  const hasSubscription =
    planSubscription && planSubscription.has_active_subscription;
  const isTrialing = planSubscription && planSubscription.status === "trialing";
  const isContentPlan = planSubscription && planSubscription.is_content_plan;
  const isStartupPlan = planSubscription && planSubscription.is_startup_plan;
  const isVCPlan = planSubscription && planSubscription.is_vc_plan;
  const isAngelInvestorPlan =
    planSubscription && planSubscription.is_angel_investor_plan;
  const hasAngelVC = planSubscription && planSubscription.has_angel_vc;

  useEffect(() => {
    // Read from router query
    const { pendingUpgrade } = router.query;
    console.log(
      `[PlanCard Query Param Effect] Checking. User: ${!!user}, Param: ${pendingUpgrade}, CheckComplete: ${
        postLoginCheckCompleteRef.current
      }`,
    );

    // Only run if user exists, param found, and check not complete
    if (
      pendingUpgrade &&
      user &&
      !postLoginCheckCompleteRef.current &&
      !hasAngelVC
    ) {
      console.log(
        `[PlanCard Query Param Effect] Conditions met! Opening modal for plan: ${pendingUpgrade}`,
      );
      // Mark as complete *first*
      postLoginCheckCompleteRef.current = true;

      setSelectedPlan(pendingUpgrade);
      setIsOpen(true);

      // Optional: Clean the URL query parameter
      const { pathname, query } = router;
      delete query.pendingUpgrade;
      router.replace({ pathname, query }, undefined, { shallow: true });
    }
    // Reset ref if user logs out to allow flow again
    else if (!user) {
      // console.log(`[PlanCard Query Param Effect] User logged out. Resetting check complete flag.`);
      postLoginCheckCompleteRef.current = false;
    }
  }, [user, router.query, router, setSelectedPlan, setIsOpen]); // Dependencies

  const handleCheckCoupon = async () => {
    if (!inputCoupon) return;
    if (loadingCheck || coupon) return;
    setCouponError(false);
    setCoupon(null);
    setloadingCheck(true);

    try {
      const res = await checkCoupon(inputCoupon);

      if (res.data.redeem_by && Date.now() > res.data.redeem_by * 1000) {
        setCouponError(true);
        setloadingCheck(false);
        return;
      }

      const selectedPlanName =
        selectedPlan === "startup"
          ? "Startup Registration"
          : selectedPlan === "vc"
            ? "VC Registration"
            : selectedPlan === "angel"
              ? "Angel Investor Registration"
              : "Content Access";

      if (res.data.applies_to && res.data.applies_to.products) {
        if (res.data.applies_to.products.includes(products[selectedPlanName])) {
          setCoupon(res.data);
        } else {
          setCouponErrorProduct(true);
        }
      } else {
        setCoupon(res.data);
      }

      setloadingCheck(false);
    } catch (error) {
      console.log(error);
      setCouponError(true);
      setloadingCheck(false);
      setCoupon(null);
    }
  };

  const handleSubscription = async (options = {}) => {
    if (disabled) {
      return;
    }
    const { successUrl = null, cancelUrl = null } = options;

    if (!user && router.pathname !== "/angel-investors/register") {
      return toLogin();
    }
    setIsOpen(false);
    setOpen(true);
    const stripe = await stripePromise;

    const selectedPlanName =
      selectedPlan === "startup"
        ? "Startup Registration"
        : selectedPlan === "vc"
          ? "VC Registration"
          : selectedPlan === "angel"
            ? "Angel Investor Registration"
            : "Content Access";

    let response;
    if (!user) {
      response = await createStripeCheckoutForGuest({
        toPlanName: selectedPlanName,
        email: emailPayloadForGuest,
        priceId: priceData[selectedPlanName].id,
        type: selectedPlan,
        coupon_id: coupon ? coupon.id : null,
        cancel_url: cancelUrl
          ? process.env.NEXT_PUBLIC_BASE_URL + cancelUrl
          : null,
        success_url:
          process.env.NEXT_PUBLIC_BASE_URL +
          (successUrl
            ? successUrl
            : selectedPlan === "startup"
              ? "startupregistration"
              : selectedPlan === "vc"
                ? "vcregistration"
                : selectedPlan === "angel"
                  ? "angelinvestorregistration"
                  : "contentaccess"),
      });
    } else {
      response = await createStripeCheckout({
        hasSubscription: hasSubscription,
        subscriptionId: planSubscription?.subscription_detail?.id,
        fromPlanName: planSubscription?.active_product?.name,
        toPlanName: selectedPlanName,
        userEmail: user?.email,
        priceId: priceData[selectedPlanName].id,
        type: selectedPlan,
        coupon_id: coupon ? coupon.id : null,
        cancel_url: cancelUrl
          ? process.env.NEXT_PUBLIC_BASE_URL + cancelUrl
          : null,
        success_url:
          process.env.NEXT_PUBLIC_BASE_URL +
          (successUrl
            ? successUrl
            : selectedPlan === "startup"
              ? "startupregistration"
              : selectedPlan === "vc"
                ? "vcregistration"
                : selectedPlan === "angel"
                  ? "angelinvestorregistration"
                  : "contentaccess"),
      });
    }

    const session = response.data;

    if (session.id) {
      const result = await stripe.redirectToCheckout({
        sessionId: session.id,
      });

      if (result.error) {
        toast.error(result.error.message);
      }
    } else {
      if (session.invoice) {
        setIsOpen(false);
        setOpen(false);
        setInvoice(session.invoice);
        setIsModalOpen(true);
      }
    }
  };

  const onRemoveCoupon = () => {
    setInputCoupon("");
    setCoupon(null);
  };

  const onCancel = () => {
    setIsOpen(false);
    setInputCoupon("");
    setCoupon(null);
    setIsCoupon(false);
  };

  const currentPath = router.asPath;

  function toLogin() {
    setOpenLoginPopup(true);
  }

  const handleUpgrade = async (type) => {
    // Prevent reopening if already open
    if (isOpen) {
      console.log(
        "[PlanCard handleUpgrade] Modal is already open. Skipping action.",
      );
      return;
    }

    if (!user) {
      // if (router.pathname === '/angel-investors/register') {
      //   const basePath = currentPath.split('?')[0];
      //   const targetUrl = `${basePath}?pendingUpgrade=${type}`;
      //   console.log(`[PlanCard handleUpgrade] Angel Register page: Preparing specific login redirect. Target URL: ${targetUrl}`);
      //   localStorage.setItem('loginRedirectTo', targetUrl);
      // } else {
      //   console.log(`[PlanCard handleUpgrade] Non-Angel Register page: Preparing generic login.`);
      //   localStorage.removeItem('loginRedirectTo');
      // }
      await handleAbandonedCart(getPlanName(type));
      if (router.pathname !== "/angel-investors/register") {
        toLogin();
      } else {
        setSelectedPlan(type);
        setIsOpen(true);
      }

      return;
    }
    setSelectedPlan(type);
    setIsOpen(true);
  };

  const getPlanName = (type) => {
    switch (type) {
      case "content":
        return "Content Access";
      case "startup":
        return "Startup Registration";
      case "vc":
        return "VC Registration";
      case "angel":
        return "Angel Investor Registration";
      default:
        return "";
    }
  };

  const onUpgrade = () => handleUpgrade("content");
  const onUpgradeStartup = () => handleUpgrade("startup");
  const onUpgradeVC = () => handleUpgrade("vc");
  const onUpgradeAngel = () => handleUpgrade("angel");

  const handleCheckboxChange = () => {
    if (isCoupon) {
      setInputCoupon("");
      setCoupon(null);
    }
    setIsCoupon(!isCoupon);
  };

  const handleCouponChange = (e) => {
    setInputCoupon(e.target.value);
  };

  const getButtonText = () => {
    if (customButtonText && onlyButton) {
      return customButtonText;
    }

    if (isStartupPlan) {
      return "You have subscribed to a different plan";
    }
    if (isVCPlan) {
      return "Go to My VC";
    }
    if (isAngelInvestorPlan) {
      return "Go to My Angel";
    }
    return "Start 7-Day Free Trial";
  };

  const handleAbandonedCart = async (planType) => {
    try {
      await createAbandonedCart({
        user_id: user?.id,
        email: user?.email || emailPayloadForGuest,
        action: "Click Upgrade Plan Button",
        location: router.pathname,
        label: planType,
      });
    } catch (error) {
      console.error("Failed to track abandoned cart:", error);
    }
  };

  return (
    <div className='relative'>
      {highlighted && !onlyButton && (
        <span className='absolute bg-pink-400 -left-[5px] -top-[5px] -right-[5px] -bottom-[5px] rounded-2xl bg-gradient-to-tr from-yellow-1 to-red-1'></span>
      )}
      {plan.plan_id === "Content Access" && (
        <p className='absolute -translate-y-full inset-x-0 -mt-2.5 text-center w-full inline-flex font-medium items-start gap-1.5 text-[11px] text-orange-400'>
          <Info weight='bold' className='text-sm translate-y-px' />
          Save {">"}20% with annual plan! (
          {convertStripeAmount(
            _priceData[plan.plan_id].unit_amount,
            currencyCode,
          ).toLocaleString("en", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          {currencyCode === "USD" ? "$" : currencyCode} /year)
        </p>
      )}
      <div
        className={
          onlyButton
            ? ""
            : highlighted
              ? `relative p-4 lg:p-5 bg-white text-background rounded-xl ${
                  !hideHighligtedGraphic ? "" : ""
                }`
              : "relative p-4 lg:p-5 bg-white text-background rounded-xl"
        }
      >
        {highlighted && !hideHighligtedGraphic && (
          <span className='absolute inline-block px-2 py-0.5 text-[10px] font-medium bg-gradient-to-tr from-yellow-1 to-red-1 rounded-full text-white top-1 right-1'>
            Recommended
          </span>
        )}
        {!onlyButton && (
          <p className='text-xl font-semibold tracking-tight lg:text-lg'>
            {plan.name}
          </p>
        )}
        <p className='mt-1 text-xs leading-snug opacity-40'>{description}</p>
        {!onlyButton && (
          <div className='flex flex-col justify-between mt-2 space-y-4 lg:mt-3 lg:space-y-0 lg:items-center lg:flex-row'>
            <div className='flex items-center justify-between w-full'>
              <div className='flex items-center space-x-2.5'>
                <p className='text-4xl font-bold leading-none tracking-tighter'>
                  {plan.plan_id === "Free"
                    ? 0
                    : Number(
                        convertStripeAmount(
                          priceData[
                            hasVCAngelSelection &&
                            (plan.plan_id === "VC Registration" ||
                              plan.plan_id === "Angel Investor Registration")
                              ? investorValue === "vc"
                                ? "VC Registration"
                                : "Angel Investor Registration"
                              : plan.plan_id
                          ].unit_amount,
                          currencyCode,
                        ).toFixed(2),
                      ).toLocaleString()}
                </p>
                <div className='flex flex-col space-y-1.5 lg:space-y-1'>
                  <p
                    className={`text-sm font-semibold leading-none ${
                      highlighted ? "text-background" : "text-background"
                    }`}
                  >
                    {currencyCode === "USD" ? "USD" : currencyCode + " "}
                  </p>
                  {plan.plan_id === "Free" && (
                    <p className='text-xs font-medium leading-none text-background/40'>
                      Free Forever!
                    </p>
                  )}
                  {plan.plan_id !== "Free" && (
                    <p
                      className={`text-xs font-medium leading-none ${
                        highlighted
                          ? "text-background/40"
                          : "text-background/40"
                      }`}
                    >
                      /
                      {plan.plan_id === "Startup Registration" ||
                      plan.plan_id === "VC Registration" ||
                      plan.plan_id === "Angel Investor Registration"
                        ? "year"
                        : plan.plan_id === "Content Access"
                          ? billingCycle === "monthly"
                            ? "month"
                            : "year"
                          : "month"}
                    </p>
                  )}
                </div>
              </div>
              {plan.plan_id === "VC Registration" && (
                <ToggleGroup
                  value={investorValue}
                  onValueChange={(value) => {
                    if (value) setInvestorValue(value);
                  }}
                  variant='outline'
                  type='single'
                  className='gap-0'
                >
                  <ToggleGroupItem
                    className='text-xs !h-auto px-2.5 border-r-0 !rounded-r-none border-black/10 shadow-sm py-1 data-[state=on]:bg-black data-[state=on]:text-white hover:bg-gray-100'
                    value='vc'
                    aria-label='Toggle bold'
                  >
                    VC
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    className='text-xs !h-auto px-2.5 border-l-0 !rounded-l-none border-black/10 shadow-sm py-1 data-[state=on]:bg-black data-[state=on]:text-white hover:bg-gray-100'
                    value='angel'
                    aria-label='Toggle italic'
                  >
                    Angel
                  </ToggleGroupItem>
                </ToggleGroup>
              )}
            </div>
          </div>
        )}
        <div className='relative mt-3'>
          {/* {!onlyButton && highlighted && !hideHighligtedGraphic && (
            <div className='hidden lg:block z-0 top-0 flex-shrink-0 absolute w-14 right-0 -translate-y-[calc(100%-28px)] aspect-[199/320]'>
              <Image
                src={LucidMascot.src}
                alt=''
                layout={"fill"}
                objectFit={"cover"}
              />
            </div>
          )} */}
          {plan.plan_id === "Free" && (
            <Button
              onClick={toLogin}
              className={`w-full px-6 !h-auto py-2.5 text-white rounded-lg !text-[13px] border disabled:bg-black/10 disabled:text-black/70 disabled:border-black/10 disabled:cursor-not-allowed ${
                highlighted
                  ? "relative z-10 bg-black hover:bg-gray-800"
                  : "relative z-10 bg-black hover:bg-gray-800"
              }`}
              disabled={
                user ||
                isContentPlan ||
                isStartupPlan ||
                isVCPlan ||
                isAngelInvestorPlan
              }
            >
              {user &&
              (isContentPlan ||
                isStartupPlan ||
                isVCPlan ||
                isAngelInvestorPlan)
                ? "Part of your current subscription"
                : user && !isContentPlan && !isStartupPlan
                  ? "Your Current Subscription"
                  : "Get Started"}
            </Button>
          )}
          {plan.plan_id === "Content Access" && (
            <>
              {hasSubscription ? (
                <Button
                  onClick={() => router.push("/account/plan")}
                  className={`w-full relative z-10 px-6 text-white ${
                    highlighted
                      ? "bg-background"
                      : isStartupPlan || isVCPlan || isAngelInvestorPlan
                        ? "bg-white/5 hover:bg-white/10"
                        : "bg-background/10 hover:bg-background/20"
                  }`}
                  disabled={isStartupPlan || isVCPlan || isAngelInvestorPlan}
                >
                  {isStartupPlan || isVCPlan || isAngelInvestorPlan
                    ? "Part of your current subscription"
                    : "Go to My Plan"}
                </Button>
              ) : (
                <Button
                  onClick={() => onUpgrade()}
                  className={`relative w-full px-6 !h-auto py-2.5 text-white rounded-lg !text-[13px] disabled:bg-black/10 disabled:text-black/70 disabled:border-black/10 disabled:cursor-not-allowed ${
                    highlighted
                      ? "bg-gradient-to-tr from-yellow-1 to-red-1"
                      : "bg-black hover:bg-black/80"
                  }`}
                >
                  {user ? "Upgrade plan" : "Buy this plan"}
                </Button>
              )}
            </>
          )}
          {plan.plan_id === "Startup Registration" && (
            <Button
              disabled={isVCPlan || isAngelInvestorPlan}
              onClick={
                isStartupPlan
                  ? () => router.push("/account/startup")
                  : onUpgradeStartup
              }
              className={`w-full relative z-10 px-6 text-white ${
                isStartupPlan ? "bg-background/10 hover:bg-background/20" : ""
              } ${
                isVCPlan || isAngelInvestorPlan
                  ? "bg-white text-gray-500 text-xs"
                  : highlighted
                    ? "bg-gradient-to-tr from-yellow-1 to-red-1"
                    : "bg-black text-white hover:bg-black/80 w-full px-6 !h-auto py-2.5 rounded-lg !text-[13px] disabled:bg-black/10 disabled:text-black/70 disabled:border-black/10 disabled:cursor-not-allowed"
              }`}
            >
              {isVCPlan || isAngelInvestorPlan
                ? "You have subscribed to a different plan"
                : isStartupPlan
                  ? "Go to My Startup"
                  : "Start 7-Day Free Trial"}
            </Button>
          )}
          {(plan.plan_id === "VC Registration" ||
            plan.plan_id === "Angel Investor Registration") && (
            <Button
              disabled={isStartupPlan || disabled}
              onClick={
                isVCPlan
                  ? () => router.push("/account/vc")
                  : isAngelInvestorPlan
                    ? () => router.push("/account/angel")
                    : hasVCAngelSelection
                      ? investorValue === "vc"
                        ? onUpgradeVC
                        : onUpgradeAngel
                      : plan.plan_id === "VC Registration"
                        ? onUpgradeVC
                        : onUpgradeAngel
              }
              className={`${
                onlyButton && !compactOnlyButton
                  ? "py-5 text-base text-white lg:pl-12 lg:pr-10 lg:py-7 lg:text-lg bg-gradient-to-tr from-yellow-1 to-red-1"
                  : `w-full relative z-10 px-6 text-white ${
                      isVCPlan ? "bg-background/10 hover:bg-background/20" : ""
                    } ${
                      isStartupPlan
                        ? "bg-white text-gray-500 text-xs"
                        : highlighted
                          ? "bg-gradient-to-tr from-yellow-1 to-red-1"
                          : "bg-black text-white hover:bg-black/80 w-full px-6 !h-auto py-2.5 rounded-lg !text-[13px] disabled:bg-black/10 disabled:text-black/70 disabled:border-black/10 disabled:cursor-not-allowed"
                    }`
              }`}
            >
              {getButtonText()}
            </Button>
          )}
          {((plan.plan_id === "VC Registration" && isVCPlan) ||
            (plan.plan_id === "Angel Investor Registration" &&
              isAngelInvestorPlan) ||
            (plan.plan_id === "Startup Registration" && isStartupPlan)) &&
            isTrialing && (
              <div className='text-center'>
                <Button
                  onClick={() => setIsOpenActivateFullsubscription(true)}
                  variant='link'
                  className='h-auto p-0 text-[11px] underline text-gray-700'
                >
                  Activate Full Subscription
                </Button>
              </div>
            )}
        </div>

        {!onlyButton && (
          <div
            className={`mt-4 ${
              highlighted ? "border-background/10" : "border-background/10"
            }`}
          >
            {/* {plan.plan_id !== "Free" && (
              <p
                className={`mb-4 font-medium text-sm ${
                  highlighted ? "text-background" : "text-background"
                }`}
              >
                {`Everything in ${
                  plan.plan_id === "Content Access"
                    ? "Free Registered User"
                    : "Researcher All-Access"
                }, and...`}
              </p>
            )} */}
            <ul
              role='list'
              className={`-my-2 text-sm leading-snug divide-y ${
                highlighted
                  ? "divide-background/5 border-background/5"
                  : "divide-background/5 border-background/5"
              }`}
            >
              {benefits?.map((item, index) => (
                <li
                  className={`flex justify-between py-2 ${
                    !item.allowed ? "bg-gray-50 shadow-inner -mx-4 px-4" : ""
                  }`}
                  key={index}
                >
                  <span
                    className={`text-xs ${
                      !item.allowed ? "opacity-30" : "opacity-60"
                    }`}
                  >
                    {item.name === "Customize Watchlists and Bookmarks" ? (
                      <span className='inline-flex flex-col leading-relaxed'>
                        <span className='inline-flex items-center'>
                          Customize Watchlists{" "}
                          <Heart weight='bold' class='size-3 ml-1 mr-1.5' />
                        </span>
                        <span className='inline-flex items-center'>
                          and Bookmarks{" "}
                          <Bookmark weight='bold' class='size-3 ml-1 mr-1.5' />
                        </span>
                      </span>
                    ) : (
                      <>{item.name}</>
                    )}
                  </span>
                  <div className='flex items-center justify-end gap-1.5 shrink-0'>
                    {item.allowed === true && (
                      <Check
                        className='text-green-500 size-4 shrink-0'
                        weight='bold'
                      />
                    )}

                    {item.allowed === "Unlimited" && (
                      <>
                        <Check
                          className='text-green-500 -translate-y-px size-4 shrink-0'
                          weight='bold'
                        />
                        <span className='text-xs text-green-500'>
                          Unlimited
                        </span>
                      </>
                    )}

                    {item.allowed === false && (
                      <X
                        className='text-red-500 size-4 shrink-0'
                        weight='bold'
                      />
                    )}

                    {typeof item.allowed === "string" &&
                      item.allowed !== "Unlimited" && (
                        <span className='text-xs text-orange-500'>
                          {item.allowed}
                        </span>
                      )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {isOpen && (
        <OnUpgradeAtStripeCheckout
          {...{
            isOpen,
            setIsOpen,
            currencyCode,
            handleCheckboxChange,
            handleCheckCoupon,
            handleSubscription,
            handleCouponChange,
            selectedPlan,
            priceData,
            isCoupon,
            coupon,
            couponError,
            setCouponError,
            onCancel,
            inputCoupon,
            loadingCheck,
            couponErrorProduct,
            onRemoveCoupon,
            planSubscription,
            priceDataMonthly,
            setBillingCycle,
            billingCycle,
            user,
            emailPayloadForGuest,
            setEmailPayloadForGuest,
          }}
        />
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Loading {hasSubscription ? "Invoice" : "Payment"}..
            </DialogTitle>
            <DialogDescription>
              <div className='mt-2'>
                {hasSubscription
                  ? "Please wait... We are creating the invoice."
                  : "Please wait...you will be redirected to the secure payment page."}
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
      <ModalActivateSubscription
        isOpen={isOpenActivateFullsubscription}
        setIsOpen={setIsOpenActivateFullsubscription}
        planSubscription={planSubscription}
      />
      {invoice && (
        <ModalWaitPaymentUpgrade
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          invoice={invoice}
          redirectTo={
            selectedPlan === "startup"
              ? "/startupregistration"
              : selectedPlan === "vc"
                ? "/vcregistration"
                : selectedPlan === "angel"
                  ? "/angelinvestorregistration"
                  : "/contentaccess"
          }
        />
      )}
    </div>
  );
};

export default PlanCard;
